import React from 'react';
import { AlertCircle, Pencil, X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { SUPPLIER_QUOTE_STATUS_LABELS, type SupplierQuote, type SupplierQuoteItem } from '../types';

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 4 });
const euro2 = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const qty = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });

type QuoteWithItems = SupplierQuote & { items: SupplierQuoteItem[] };

interface Props {
  ausschreibungId: string;
  quoteId: string | null;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}

/** Drawer mit Kopfdaten (Lieferant editierbar) und allen gelesenen Zeilen eines Angebots. */
export const TenderQuoteDetailDrawer: React.FC<Props> = ({ ausschreibungId, quoteId, onClose, onChanged }) => {
  const [quote, setQuote] = React.useState<QuoteWithItems | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [supplierName, setSupplierName] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!quoteId) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/quotes/${encodeURIComponent(quoteId)}`, {
      credentials: 'include',
    })
      .then(async (res) => {
        const body = (await res.json().catch(() => ({}))) as QuoteWithItems | { error?: string };
        if (!res.ok) throw new Error((body as { error?: string }).error || 'Angebot konnte nicht geladen werden.');
        if (!cancelled) {
          setQuote(body as QuoteWithItems);
          setSupplierName((body as QuoteWithItems).supplier_name ?? '');
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ausschreibungId, quoteId]);

  const saveSupplier = async () => {
    if (!quote) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/quotes/${encodeURIComponent(quote.id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ supplier_name: supplierName }),
      });
      const body = (await res.json().catch(() => ({}))) as SupplierQuote | { error?: string };
      if (!res.ok) throw new Error((body as { error?: string }).error || 'Speichern fehlgeschlagen.');
      setQuote((prev) => (prev ? { ...prev, ...(body as SupplierQuote) } : prev));
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const supplierDirty = quote != null && supplierName.trim() !== (quote.supplier_name ?? '').trim();
  const [editingSupplier, setEditingSupplier] = React.useState(false);
  React.useEffect(() => {
    setEditingSupplier(false);
  }, [quoteId]);

  /** Kopfnotizen der Extraktion in Lieferung / Zahlung / Abweichung zerlegen. */
  const headerFacts = React.useMemo(() => {
    const text = quote?.header_notes ?? '';
    const pick = (label: string) => {
      const m = text.match(new RegExp(`${label}:\\s*(.*?)(?=\\s(?:Lieferung|Zahlung):|\\sSumme der Zeilen weicht|$)`, 's'));
      return m ? m[1].trim() : '';
    };
    return { delivery: pick('Lieferung'), payment: pick('Zahlung') };
  }, [quote?.header_notes]);

  const isBauseits = (name: string, description: string | null | undefined) =>
    /\bbauseits\b/i.test(`${name} ${description ?? ''}`);
  const hasAlternatives = !!quote?.items.some((it) => it.is_alternative);
  const hasBauseits = !!quote?.items.some((it) => isBauseits(it.item_name, it.description));
  const hasUncertain = !!quote?.items.some((it) => (it.extraction_confidence ?? 1) < 0.7);
  // Spalten nur zeigen, wenn mindestens eine Zeile etwas enthält.
  const showListPrice = !!quote?.items.some((it) => it.list_price != null);
  const showDiscount = !!quote?.items.some((it) => it.discount_percent != null);
  const showNotes = !!quote?.items.some((it) => (it.delivery_time && it.delivery_time.trim()) || (it.match_note && it.match_note.trim()));

  return (
    <AppModal open={quoteId != null} onClose={onClose} panelClassName="md:max-w-[94vw] md:w-full">
      <div className="flex flex-col max-h-[90vh]">
        <div className="flex items-start justify-between gap-3 border-b border-app-border px-6 py-4">
          <div className="min-w-0">
            {editingSupplier && quote ? (
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && supplierDirty) void saveSupplier().then(() => setEditingSupplier(false));
                    if (e.key === 'Escape') {
                      setSupplierName(quote.supplier_name ?? '');
                      setEditingSupplier(false);
                    }
                  }}
                  className="h-9 w-80 max-w-full rounded-md border border-app-border bg-app-surface px-2 text-base font-semibold text-app-text"
                  placeholder="Name des Großhändlers"
                />
                <button
                  type="button"
                  onClick={() => void saveSupplier().then(() => setEditingSupplier(false))}
                  disabled={!supplierDirty || saving}
                  className="h-9 inline-flex items-center gap-1.5 rounded-md bg-pipedrive-blue px-3 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? <LoadingSpinner size="xs" /> : null}
                  Speichern
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSupplierName(quote.supplier_name ?? '');
                    setEditingSupplier(false);
                  }}
                  className="h-9 rounded-md px-3 text-sm font-medium text-app-text-muted hover:bg-app-hover"
                >
                  Abbrechen
                </button>
              </div>
            ) : (
              <h2 className="flex items-center gap-2 text-lg font-semibold text-app-text min-w-0">
                <span className="truncate">{quote?.supplier_name || 'Lieferantenangebot'}</span>
                {quote?.quote_number ? <span className="text-app-text-subtle font-normal shrink-0">· {quote.quote_number}</span> : null}
                {quote && (
                  <button
                    type="button"
                    onClick={() => setEditingSupplier(true)}
                    className="shrink-0 p-1 rounded text-app-text-subtle hover:bg-app-hover hover:text-pipedrive-blue"
                    title="Lieferant ändern"
                    aria-label="Lieferant ändern"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
              </h2>
            )}
            {quote && (
              <p className="text-sm text-app-text-subtle mt-0.5">
                {SUPPLIER_QUOTE_STATUS_LABELS[quote.status]}
                {quote.quote_date ? ` · vom ${new Date(quote.quote_date).toLocaleDateString('de-DE')}` : ''}
                {quote.valid_until ? ` · gültig bis ${new Date(quote.valid_until).toLocaleDateString('de-DE')}` : ''}
                {quote.net_total != null ? ` · Netto ${euro2.format(quote.net_total)}` : ''}
                {quote.price_basis !== 'unknown' ? ` · Preisbasis ${quote.price_basis}` : ''}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-app-hover text-slate-400" aria-label="Schließen">
            <X className="w-5 h-5" />
          </button>
        </div>

        {quote && (headerFacts.delivery || headerFacts.payment || quote.status_error || hasAlternatives || hasBauseits || hasUncertain) && (
          <div className="flex flex-wrap items-start gap-x-8 gap-y-2 border-b border-app-border px-6 py-3 bg-app-surface-muted/60 text-sm">
            {headerFacts.delivery && (
              <div className="min-w-0 text-app-text">
                <span className="text-app-text-subtle">Lieferung: </span>
                {headerFacts.delivery}
              </div>
            )}
            {headerFacts.payment && (
              <div className="min-w-0 text-app-text">
                <span className="text-app-text-subtle">Zahlung: </span>
                {headerFacts.payment}
              </div>
            )}
            {quote.status_error && (
              <div className="min-w-0 max-w-md">
                <div className="text-xs text-app-text-subtle">Fehler</div>
                <div className="text-red-700 inline-flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {quote.status_error}
                </div>
              </div>
            )}
            {(hasAlternatives || hasBauseits || hasUncertain) && (
              <div className="ml-auto flex flex-wrap items-center gap-4 text-app-text-muted">
                {hasAlternatives && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm bg-amber-100 border border-amber-200" /> Alternativposition
                  </span>
                )}
                {hasBauseits && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm bg-app-surface-muted border border-app-border" /> bauseits
                  </span>
                )}
                {hasUncertain && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-amber-600 font-semibold">?</span> Extraktion unsicher
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div className="app-scrollbar min-h-0 flex-1 overflow-auto">
          {error && (
            <div className="mx-6 my-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          {loading ? (
            <div className="py-8 grid place-items-center">
              <LoadingSpinner />
            </div>
          ) : quote && quote.items.length === 0 ? (
            <p className="text-sm text-app-text-muted px-6 py-6">
              {quote.status === 'ready' ? 'Keine Zeilen gelesen.' : 'Noch keine Zeilen; das Angebot wird gelesen.'}
            </p>
          ) : quote ? (
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-app-surface text-app-text-subtle z-10">
                <tr>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">#</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">LV-Pos</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Art.-Nr.</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Hersteller</th>
                  <th className="px-3 py-2 text-left font-medium">Bezeichnung</th>
                  <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Menge</th>
                  {showListPrice && <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Liste</th>}
                  {showDiscount && <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Rabatt</th>}
                  <th className="px-3 py-2 text-right font-medium whitespace-nowrap">EK netto</th>
                  <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Gesamt</th>
                  {showNotes && <th className="px-3 py-2 text-left font-medium">Hinweis</th>}
                </tr>
              </thead>
              <tbody>
                {quote.items.map((it) => {
                  const description = it.description && it.description.trim() !== it.item_name.trim() ? it.description : '';
                  const notes = [it.delivery_time, it.match_note].filter((n): n is string => !!n && n.trim().length > 0);
                  return (
                    <tr
                      key={it.id}
                      className={`border-t border-app-border align-top ${
                        it.is_alternative ? 'bg-amber-50' : isBauseits(it.item_name, it.description) ? 'bg-app-surface-muted text-app-text-muted' : ''
                      }`}
                      title={
                        it.is_alternative
                          ? 'Alternativposition des Händlers'
                          : isBauseits(it.item_name, it.description)
                            ? 'bauseits: wird vom Händler nicht geliefert'
                            : undefined
                      }
                    >
                      <td
                        className="px-3 py-2 tabular-nums text-app-text-subtle whitespace-nowrap"
                        title={[it.page ? `Seite ${it.page}` : '', (it.extraction_confidence ?? 1) < 0.7 ? 'Extraktion unsicher, bitte prüfen' : '']
                          .filter(Boolean)
                          .join(' · ') || undefined}
                      >
                        {it.printed_line_no || it.line_no}
                        {(it.extraction_confidence ?? 1) < 0.7 ? <span className="text-amber-600"> ?</span> : null}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{it.lv_oz_ref ?? ''}</td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{it.article_number ?? ''}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-app-text-muted">{it.manufacturer ?? ''}</td>
                      <td className="px-3 py-2 min-w-[18rem]">
                        <span className="font-medium text-app-text">{it.item_name}</span>
                        {description && <span className="block text-app-text-subtle">{description}</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                        {it.quantity != null ? qty.format(it.quantity) : ''} {it.unit ?? ''}
                        {it.price_unit_qty !== 1 ? <span className="text-app-text-subtle"> · PE {it.price_unit_qty}</span> : null}
                      </td>
                      {showListPrice && (
                        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{it.list_price != null ? euro.format(it.list_price) : ''}</td>
                      )}
                      {showDiscount && (
                        <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{it.discount_percent != null ? `${it.discount_percent} %` : ''}</td>
                      )}
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-app-text whitespace-nowrap">
                        {it.unit_price_net != null ? euro.format(it.unit_price_net) : ''}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{it.line_total_net != null ? euro2.format(it.line_total_net) : ''}</td>
                      {showNotes && <td className="px-3 py-2 text-app-text-subtle max-w-sm">{notes.join(' · ')}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </div>
      </div>
    </AppModal>
  );
};
